/**
 * Dynamic pricing components section that displays all matched
 * pricing components with toggle and input options for customers
 */

import React from "react";
import {
  Typography,
  List,
  Checkbox,
  InputNumber,
  Space,
  Tooltip,
  Tag,
  Collapse,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatEuro } from "../PricingCalculatorHelpers";
import type { PricingComponentResult } from "../hooks/useTemplatePricing";

const { Text } = Typography;
const { Panel } = Collapse;

interface AllComponentsSectionProps {
  components: PricingComponentResult[];
  onToggleComponent: (componentId: number, enabled: boolean) => void;
  onUpdateValue: (componentId: number, value: number) => void;
}

export const AllComponentsSection: React.FC<AllComponentsSectionProps> = ({
  components,
  onToggleComponent,
  onUpdateValue,
}) => {
  const { t } = useTranslation();

  // Group components by category
  const groupedComponents = components.reduce((groups, component) => {
    const group = component.categoryGroup;
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(component);
    return groups;
  }, {} as Record<string, PricingComponentResult[]>);

  // Category titles
  const categoryTitles: Record<string, string> = {
    transport: t(
      "bookingWizard.pricingCalculator.transportCostsSectionTitle",
      "Transport"
    ),
    customs: t(
      "bookingWizard.pricingCalculator.customsHandlingSectionTitle",
      "Customs & Handling"
    ),
    handling: t(
      "bookingWizard.pricingCalculator.handlingSectionTitle",
      "Handling & Warehouse"
    ),
    delivery: t(
      "bookingWizard.pricingCalculator.deliveryCostsSectionTitle",
      "Delivery"
    ),
    other: t("bookingWizard.pricingCalculator.otherCosts", "Other Costs"),
  };

  // Get calculation type display label
  const getCalculationTypeLabel = (calculationType: string): string => {
    const labels: Record<string, string> = {
      per_carton: t("pricingCalculator.perCarton", "per carton"),
      per_pallet: t("pricingCalculator.perPallet", "per pallet"),
      per_unique_product: t("pricingCalculator.perProduct", "per product"),
      per_kg: t("pricingCalculator.perKg", "per kg"),
      per_cbm: t("pricingCalculator.perCbm", "per CBM"),
      per_shipment: t("pricingCalculator.perShipment", "per shipment"),
      per_container: t("pricingCalculator.perContainer", "per container"),
      fixed: t("pricingCalculator.fixed", "fixed"),
      percentage: t("pricingCalculator.percentage", "percentage"),
    };

    return labels[calculationType] || calculationType;
  };

  // Render a single component
  const renderComponent = (component: PricingComponentResult) => {
    const isOptional = component.isOptional;
    const canEdit = component.canCustomerEdit;

    return (
      <List.Item
        key={component.id}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "12px 0",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isOptional && (
              <Checkbox
                checked={component.isEnabled}
                onChange={(e) =>
                  onToggleComponent(component.componentId, e.target.checked)
                }
              />
            )}
            <Space direction="vertical" size={2} style={{ flex: 1 }}>
              <Space>
                <Text strong={!isOptional}>
                  {component.name}
                </Text>
                {component.description && (
                  <Tooltip title={component.description}>
                    <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                  </Tooltip>
                )}
              </Space>
              <Space size={4}>
                <Tag
                  color="blue"
                  style={{ fontSize: "11px", margin: 0 }}
                >
                  {getCalculationTypeLabel(component.calculationType)}
                </Tag>
                {component.quantity > 1 && (
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    {formatEuro(component.value)} × {component.quantity}
                  </Text>
                )}
              </Space>
            </Space>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 100,
          }}
        >
          {canEdit && component.isEnabled ? (
            <InputNumber
              value={component.value}
              onChange={(value) =>
                value !== null && onUpdateValue(component.componentId, value)
              }
              min={0}
              step={1}
              precision={2}
              prefix="€"
              style={{ width: 100 }}
              size="small"
            />
          ) : (
            <Text
              strong
              type={component.isEnabled ? undefined : "secondary"}
              style={{ textAlign: "right", minWidth: 80 }}
            >
              {component.isEnabled
                ? formatEuro(component.totalPrice)
                : formatEuro(0)}
            </Text>
          )}
        </div>
      </List.Item>
    );
  };

  // Render a category group
  const renderCategoryGroup = (
    category: string,
    categoryComponents: PricingComponentResult[]
  ) => {
    if (categoryComponents.length === 0) return null;

    return (
      <div key={category} style={{ marginBottom: 16 }}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          {categoryTitles[category] || category}
        </Text>
        <List
          size="small"
          itemLayout="horizontal"
          dataSource={categoryComponents}
          renderItem={renderComponent}
          style={{ marginTop: 8 }}
        />
      </div>
    );
  };

  if (components.length === 0) {
    return (
      <div style={{ marginBottom: 16, textAlign: "center", padding: 24 }}>
        <Text type="secondary">
          {t(
            "bookingWizard.pricingCalculator.noComponentsFound",
            "No pricing components found. Please ensure all booking details are filled in."
          )}
        </Text>
      </div>
    );
  }

  // Render all category groups
  const categoryOrder: Array<
    "transport" | "customs" | "handling" | "delivery" | "other"
  > = ["transport", "customs", "handling", "delivery", "other"];

  return (
    <div>
      {categoryOrder.map((category) => {
        const categoryComponents = groupedComponents[category] || [];
        return renderCategoryGroup(category, categoryComponents);
      })}
    </div>
  );
};
















